import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  objectFit?: "cover" | "contain" | "fill" | "none";
  onLoad?: () => void;
  onError?: () => void;
  "data-testid"?: string;
}

const PLACEHOLDER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3C/svg%3E";

function getOptimizedSrc(src: string, width?: number): string {
  if (!src) return PLACEHOLDER_SVG;
  if (src.includes("unsplash.com")) {
    const url = new URL(src);
    if (width) {
      url.searchParams.set("w", String(width));
    }
    url.searchParams.set("q", "75");
    url.searchParams.set("fm", "webp");
    url.searchParams.set("auto", "format");
    return url.toString();
  }
  return src;
}

function generateSrcSet(src: string): string | undefined {
  if (!src || !src.includes("unsplash.com")) return undefined;
  const widths = [320, 640, 960, 1280, 1920];
  return widths
    .map((w) => `${getOptimizedSrc(src, w)} ${w}w`)
    .join(", ");
}

export function OptimizedImage({
  src,
  alt,
  className,
  width,
  height,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  priority = false,
  objectFit = "cover",
  onLoad,
  onError,
  ...props
}: OptimizedImageProps) {
  const [loaded, setLoaded] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const srcSet = generateSrcSet(src);
  const optimizedSrc = getOptimizedSrc(src, width);

  const objectFitClass = {
    cover: "object-cover",
    contain: "object-contain",
    fill: "object-fill",
    none: "object-none",
  }[objectFit];

  return (
    <img
      ref={imgRef}
      src={error ? PLACEHOLDER_SVG : optimizedSrc}
      srcSet={error ? undefined : srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      {...(priority ? ({ fetchpriority: "high" } as Record<string, string>) : {})}
      className={cn(
        objectFitClass,
        "transition-opacity duration-300",
        loaded ? "opacity-100" : "opacity-0",
        className
      )}
      onLoad={() => {
        setLoaded(true);
        onLoad?.();
      }}
      onError={() => {
        setError(true);
        onError?.();
      }}
      data-testid={props["data-testid"]}
    />
  );
}

export default OptimizedImage;
